import {
  toWSDateString,
  WSActions,
  WSAlert,
  WSCard,
  WSDataItem,
  WSFlexBox,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import {
  Batch,
  BatchStatus,
  BatchSummaryExpand,
  PayableImportBatchConfiguration
} from "@wingspanhq/wingspan-api-sdk";
import {
  FlatfileProvider,
  Space,
  useFlatfile,
  Workbook
} from "@flatfile/react";
import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { CSVLink } from "react-csv";

import { queriesBatches } from "@query";
import { WSQueryCache } from "@ws-react-query";
import { ErrorAlert } from "@components/ErrorAlert";
import { WSQueries } from "../../../../query/WSQuery";
import { IS_PRODUCTION_ENV } from "../../../../shared/constants/environment";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { useDefaultCurrency } from "../../../../shared/hooks/useDefaultCurrency";
import { IdempotencyKey } from "../../../../v3/utils/idempotencyKey";
import { flatfileProviderConfig } from "../../utils/flatfileThemeConfig";
import {
  routeHelpersImportPayables,
  routeHelpersImportPayablesBatch,
  routeHelpersImportPayablesBatchHistory
} from "../../paths";
import { selectorPayableImportBatchName } from "../../selectors/selectorPayableImportBatchName";
import { selectorPayableImportBatchStatusPill } from "../../selectors/selectorPayableImportBatchStatusPill";
import { useModalConfirmPayableImportBatchDelete } from "../../components/ModalConfirmPayableImportBatchDelete";
import { useModalEditPayableImportBatchName } from "../../components/ModalEditPayableImportBatchName";
import { usePayablesImportSpaceMetadata } from "../../hooks/usePayablesImportSpaceMetadata";
import { buildPayableImportSheetConfig } from "./payableImportSheet";
import { usePayableImportUpload } from "./usePayableImportUpload";
import { useDownloadPayableImportErrorsCSV } from "./useDownloadPayableImportErrorsCSV";

import styles from "./styles.module.scss";

const PayableBatchDetails: React.FC<{
  batch: Batch;
  openPortalOnMount?: boolean;
}> = props => {
  const batchId = props.batch.id as string;
  const currency = useDefaultCurrency();
  const [uploadError, setUploadError] = useState<unknown>(undefined);
  const errorsCSVLinkRef = useRef<any>(null);

  const history = useHistory();
  const location = useLocation();
  const goToPayableBatches = routeHelpersImportPayables.useGoTo();
  const goToHistory = routeHelpersImportPayablesBatchHistory.useGoTo();
  const modalConfirmDelete = useModalConfirmPayableImportBatchDelete();
  const modalEditName = useModalEditPayableImportBatchName();

  const querySummary = queriesBatches.getBatchSummary.useQuery(
    { batchId, expand: [BatchSummaryExpand.BREAKDOWN] },
    { refetchOnMount: true }
  );

  const [processBatch, processBatchMeta] =
    queriesBatches.processBatch.useMutation();

  const submitRows = usePayableImportUpload(batchId);
  const spaceMetadata = usePayablesImportSpaceMetadata(batchId);
  const { errorsCSVFileProps, hasErrors } =
    useDownloadPayableImportErrorsCSV(batchId);
  const { openPortal, closePortal } = useFlatfile();

  // The upload posts items with `batchesApi.addBatchItem` directly rather than
  // through a registry mutation, so nothing invalidates the sibling reads —
  // the failed-item list behind "Download errors" would keep serving its
  // pre-upload cache.
  const refreshAfterUpload = () =>
    Promise.all([
      querySummary.refetch(),
      WSQueryCache.invalidateQueries(
        queriesBatches.listBatchItems.queryKeyBase
      ),
      WSQueryCache.invalidateQueries(queriesBatches.getBatch.queryKeyBase)
    ]);

  // The start screen hands off with `showBatchCreatedModal`; v1 showed a modal
  // whose acknowledge button opened the portal. Without it the create flow
  // dead-ends on an empty batch, so go straight to the upload instead.
  useEffect(() => {
    if (props.openPortalOnMount) {
      // Drop the hand-off flag from history state, or every reload and every
      // back-navigation to this batch reopens the portal.
      history.replace(location.pathname);
      openPortal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const progress = querySummary.data?.progress;
  const breakdown = querySummary.data?.breakdown;
  const itemCount = progress?.total;
  const isEmptyBatch = itemCount === 0;
  const isCreated = props.batch.status === BatchStatus.CREATED;
  const configuration = props.batch._configuration as
    | PayableImportBatchConfiguration
    | undefined;
  // The API defaults this to the sentinel `StatusUnspecified`, which is not a
  // member of the SDK's `PayableStatus` enum and means "the backend picks" —
  // rendering it raw would show users an internal token.
  const payableStatusLabel =
    !configuration?.payableStatus ||
    String(configuration.payableStatus) === "StatusUnspecified"
      ? "Default"
      : configuration.payableStatus;

  const onCompleteImport = async () => {
    const idempotencyKey = new IdempotencyKey({ batchId, action: "process" });
    await processBatch(
      { batchId, idempotencyKey: idempotencyKey.value },
      { onSuccess: () => idempotencyKey.destroy() }
    );
  };

  const onDelete = async () => {
    const confirmed = await modalConfirmDelete.open({
      batchId,
      batchName: selectorPayableImportBatchName(props.batch)
    });
    if (confirmed) {
      goToPayableBatches();
    }
  };

  return (
    <WSPage>
      {/*
        No `namespace` yet, so no listener claims this space and the `Workbook`
        below stays the upload mechanism. Adding `namespace: "payablesImport"`
        is the switch to the server-side importer — do it only once that
        listener reads a v3 `batchId`, and drop the `Workbook` in the same
        change, or both sides define the workbook.
      */}
      <Space config={{ name: "Payables Upload", metadata: spaceMetadata }}>
        <Workbook
          config={{
            name: "Payables upload",
            sheets: [buildPayableImportSheetConfig()]
          }}
          onSubmit={async ({ sheet }) => {
            setUploadError(undefined);
            try {
              // Only rows that passed the blueprint's own validation — the
              // rest stay in the portal for the user to correct.
              const records = await sheet.validData();
              const result = await submitRows(
                (records?.records ?? []).map(
                  (record: { values: Record<string, { value: unknown }> }) =>
                    Object.fromEntries(
                      Object.entries(record.values).map(([key, cell]) => [
                        key,
                        cell?.value
                      ])
                    )
                )
              );
              await refreshAfterUpload();
              if (result.rejected.length === 0) {
                closePortal();
              } else {
                setUploadError(
                  new Error(
                    `${result.submitted} rows uploaded, ${result.rejected.length} could not be matched to a contractor or were rejected. Fix them in your spreadsheet and upload again.`
                  )
                );
              }
            } catch (error) {
              setUploadError(error);
            }
          }}
        />
      </Space>

      {hasErrors && (
        <CSVLink
          ref={errorsCSVLinkRef}
          className={styles.hiddenButton}
          {...errorsCSVFileProps}
        />
      )}

      <WSPageToolbar
        breadcrumbs={[
          {
            label: "Bulk imports (payables)",
            onClick: () => goToPayableBatches()
          },
          { label: selectorPayableImportBatchName(props.batch) }
        ]}
      />

      <WSFlexBox.CenterY mb="XL">
        <WSText.Heading3 weight="medium" mr="L">
          {selectorPayableImportBatchName(props.batch)}
        </WSText.Heading3>
        <WSPill
          {...selectorPayableImportBatchStatusPill({
            batch: props.batch,
            itemCount,
            failedCount: progress?.failed,
            showIcon: true
          })}
        />
      </WSFlexBox.CenterY>

      <WSGrid gutter="XL">
        <WSGrid.Item span={{ s: "12", m: "8" }}>
          <WSList gap="XL">
            <ErrorAlert context="BulkBatchImportError" error={uploadError} />
            <ErrorAlert
              context="BulkBatchStartImport"
              error={processBatchMeta.error}
            />

            {isCreated && isEmptyBatch && (
              <WSAlert
                theme="info"
                icon="alert-circle"
                title="Nothing uploaded yet"
              >
                Upload your formatted CSV or Excel spreadsheet to add payables
                to this batch, then complete the import.
              </WSAlert>
            )}

            {props.batch.status === BatchStatus.FAILED && (
              <WSAlert theme="error" icon="alert-circle" title="Import failed">
                No rows in this batch were imported. Download the errors to see
                what went wrong, then start a new batch.
              </WSAlert>
            )}

            <WSCard header="Import summary" divider>
              <WSList gap="XL">
                <WSDataItem
                  inline
                  label="Rows in batch"
                  value={progress?.total ?? "--"}
                />
                <WSDataItem
                  inline
                  label="Imported"
                  value={progress?.completed ?? "--"}
                />
                <WSDataItem
                  inline
                  label="Failed"
                  value={progress?.failed ?? "--"}
                />
                {breakdown && "netAmount" in breakdown && (
                  <>
                    <WSDataItem
                      inline
                      label="Payables"
                      value={`${breakdown.payablesCount} · ${formatMoney(breakdown.payablesAmount, currency)}`}
                    />
                    <WSDataItem
                      inline
                      label="Deductions"
                      value={`${breakdown.deductionsCount} · ${formatMoney(breakdown.deductionsAmount, currency)}`}
                    />
                    <WSDataItem
                      inline
                      label="Net import amount"
                      value={formatMoney(breakdown.netAmount, currency)}
                    />
                    <WSDataItem
                      inline
                      label="Contractors impacted"
                      value={breakdown.payeesImpactedCount}
                    />
                  </>
                )}
              </WSList>
            </WSCard>

            <WSCard header="Details" divider>
              <WSList gap="XL">
                <WSDataItem
                  inline
                  label="Created"
                  value={
                    props.batch.events?.createdAt
                      ? toWSDateString(props.batch.events.createdAt)
                      : "--"
                  }
                />
                <WSDataItem
                  inline
                  label="Import strategy"
                  value={configuration?.processingStrategy ?? "--"}
                />
                <WSDataItem
                  inline
                  label="Initial status of created payables"
                  value={payableStatusLabel}
                />
                {props.batch.metadata?.notes && (
                  <WSDataItem
                    label="Notes"
                    value={props.batch.metadata.notes}
                  />
                )}
              </WSList>
            </WSCard>

            <WSActions
              alignment="left"
              buttons={[
                ...(isCreated
                  ? [
                      {
                        name: "uploadSpreadsheet",
                        // Never "View upload": the in-browser portal persists no
                        // space id, so every open starts a fresh session rather
                        // than reopening the previous one. No longer an API
                        // limit — `updateBatch` can write one to `metadata` —
                        // it lands with the server-side space (WIN-15867).
                        label:
                          itemCount && itemCount > 0
                            ? "Upload more rows"
                            : "Upload spreadsheet",
                        onClick: () => openPortal()
                      },
                      {
                        name: "completeImport",
                        label: "Complete import",
                        // Unknown until the summary lands — importing an empty
                        // batch is a no-op the user cannot undo.
                        disabled: !itemCount,
                        loading: processBatchMeta.isLoading,
                        onClick: onCompleteImport
                      }
                    ]
                  : []),
                {
                  name: "editName",
                  kind: "Secondary" as const,
                  label: "Edit name",
                  onClick: () => modalEditName.open({ batch: props.batch })
                },
                ...(hasErrors
                  ? [
                      {
                        name: "downloadErrors",
                        kind: "Secondary" as const,
                        label: "Download errors",
                        onClick: () => errorsCSVLinkRef.current?.link?.click()
                      }
                    ]
                  : []),
                {
                  name: "deleteBatch",
                  kind: "Secondary" as const,
                  destructive: true,
                  label: "Delete batch",
                  onClick: onDelete
                }
              ]}
            />
          </WSList>
        </WSGrid.Item>

        <WSGrid.Item span={{ s: "12", m: "4" }}>
          <WSCard
            header={{
              label: "Batch history",
              value: {
                action: {
                  label: "See all",
                  onClick: () => goToHistory({ batchId }),
                  type: "button"
                }
              }
            }}
          >
            <WSText.ParagraphSm color="gray500">
              Created{" "}
              {props.batch.events?.createdAt
                ? toWSDateString(props.batch.events.createdAt, "timeframe")
                : "--"}
            </WSText.ParagraphSm>
          </WSCard>
        </WSGrid.Item>
      </WSGrid>
    </WSPage>
  );
};

export const RouteImportPayableBatchDetails: React.FC = () => {
  useBrowserPageTitle("Bulk imports: Payable batch");
  const { params, state } = routeHelpersImportPayablesBatch.useData();
  const queryBatch = queriesBatches.getBatch.useQuery(
    { batchId: params.batchId },
    { refetchOnMount: true }
  );

  return (
    <FlatfileProvider
      publishableKey={process.env.REACT_APP_FLATFILE_PUBLISHABLE_KEY as string}
      config={{ debug: !IS_PRODUCTION_ENV, ...flatfileProviderConfig }}
    >
      <WSQueries queries={{ queryBatch }}>
        {({ queryBatchData: batch }) => (
          <PayableBatchDetails
            batch={batch}
            openPortalOnMount={state?.showBatchCreatedModal}
          />
        )}
      </WSQueries>
    </FlatfileProvider>
  );
};
